import {
    Modal,
    Pressable,
    StyleSheet,
    useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ScrollView, Text, YStack } from 'tamagui';
import { LinearGradient } from 'tamagui/linear-gradient';

import PremiumLogo from '../../../assets/premium-logo.svg';
import { useThemeColors } from '../../themes/SmoothieColorTokens';
import { FontFamilyName } from '../../themes/SmoothieFontTokens';
import { Button, ButtonSize, ButtonVariant } from '../buttons/Button';
import { Shadows } from '../Shadows';
import { BodyMediumRegular } from '../text/Typography';

const SCREEN_INSET = 24;
const CARD_MAX_WIDTH = 354;
const CARD_RADIUS = 40;
const BACKDROP_COLOR = 'rgba(0, 0, 0, 0.5)';
const LIST_BOX_COLOR = 'rgba(0, 0, 0, 0.33)';

const GRADIENT_START = { x: -0.44, y: 0.25 };
const GRADIENT_END = { x: 1.44, y: 0.75 };
const GRADIENT_LOCATIONS: [number, number] = [0.173, 0.976];

const SERIF_TEXT_SHADOW = {
    textShadowColor: 'rgba(0, 0, 0, 0.06)',
    textShadowOffset: { width: 0, height: 4 },
    textShadowRadius: 8,
};

export interface PremiumInfoSheetAction {
    text: string;
    onPress: () => void;
}

export interface PremiumInfoSheetProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    subtitle?: string;
    listTitle?: string;
    listItems?: string[];
    primaryAction: PremiumInfoSheetAction;
    secondaryAction?: PremiumInfoSheetAction;
    dismissOnOverlayPress?: boolean;
}

/**
 * A floating, gold-branded sheet for Premium moments (e.g. welcoming a member
 * after upgrading or starting a trial). Always renders in the light palette so
 * the brand surface looks the same in light and dark mode.
 */
export const PremiumInfoSheet = ({
    isOpen,
    onClose,
    title,
    subtitle,
    listTitle,
    listItems,
    primaryAction,
    secondaryAction,
    dismissOnOverlayPress = true,
}: PremiumInfoSheetProps) => {
    const { top, bottom } = useSafeAreaInsets();
    const { height: screenHeight } = useWindowDimensions();
    const { getPrimitiveColor } = useThemeColors();

    const textColor = getPrimitiveColor('$peachLight');
    const gradientColors: [string, string] = [
        getPrimitiveColor('$peachDark'),
        getPrimitiveColor('$peachDarkest'),
    ];
    const bottomInset = Math.max(bottom, SCREEN_INSET);
    const cardMaxHeight = screenHeight - top - SCREEN_INSET - bottomInset;
    const hasList = listItems != null && listItems.length > 0;

    return (
        <Modal
            visible={isOpen}
            transparent
            animationType="fade"
            statusBarTranslucent
            onRequestClose={onClose}
        >
            <YStack
                flex={1}
                justifyContent="center"
                alignItems="center"
                paddingHorizontal={SCREEN_INSET}
                paddingTop={top + SCREEN_INSET}
                paddingBottom={bottomInset}
            >
                <Pressable
                    style={[
                        StyleSheet.absoluteFill,
                        { backgroundColor: BACKDROP_COLOR },
                    ]}
                    onPress={dismissOnOverlayPress ? onClose : undefined}
                    accessibilityRole="button"
                    accessibilityLabel="Close"
                />
                <YStack
                    width="100%"
                    maxWidth={CARD_MAX_WIDTH}
                    maxHeight={cardMaxHeight}
                    borderRadius={CARD_RADIUS}
                    style={Shadows.LARGE}
                    animation="sheet"
                    enterStyle={{ y: 24 }}
                >
                    <YStack
                        borderRadius={CARD_RADIUS}
                        overflow="hidden"
                        flexShrink={1}
                    >
                        <LinearGradient
                            colors={gradientColors}
                            locations={GRADIENT_LOCATIONS}
                            start={GRADIENT_START}
                            end={GRADIENT_END}
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={0}
                        />
                        <ScrollView
                            bounces={false}
                            showsVerticalScrollIndicator={false}
                            flexGrow={0}
                        >
                            <YStack
                                alignItems="center"
                                gap={24}
                                paddingHorizontal={8}
                                paddingVertical={32}
                            >
                                <PremiumLogo width={80} height={80} />
                                <YStack gap={16} width="100%">
                                    <Text
                                        fontSize={40}
                                        lineHeight={40}
                                        letterSpacing={-2}
                                        textAlign="center"
                                        color={textColor}
                                        style={{
                                            fontFamily:
                                                FontFamilyName.SourceSerifSemiBold,
                                            ...SERIF_TEXT_SHADOW,
                                        }}
                                    >
                                        {title}
                                    </Text>
                                    {subtitle != null && subtitle !== '' && (
                                        <BodyMediumRegular
                                            color={textColor}
                                            textAlign="center"
                                            paddingHorizontal={16}
                                        >
                                            {subtitle}
                                        </BodyMediumRegular>
                                    )}
                                </YStack>
                            </YStack>
                            {hasList && (
                                <YStack paddingHorizontal={24}>
                                    <YStack
                                        gap={16}
                                        padding={24}
                                        borderRadius={24}
                                        backgroundColor={LIST_BOX_COLOR}
                                    >
                                        {listTitle != null &&
                                            listTitle !== '' && (
                                                <Text
                                                    fontSize={20}
                                                    lineHeight={20}
                                                    letterSpacing={-1}
                                                    textAlign="center"
                                                    color={textColor}
                                                    style={{
                                                        fontFamily:
                                                            FontFamilyName.SourceSerifSemiBold,
                                                        ...SERIF_TEXT_SHADOW,
                                                    }}
                                                >
                                                    {listTitle}
                                                </Text>
                                            )}
                                        <YStack gap={6}>
                                            {listItems.map((item) => (
                                                <BodyMediumRegular
                                                    key={item}
                                                    color={textColor}
                                                    textAlign="center"
                                                >
                                                    {item}
                                                </BodyMediumRegular>
                                            ))}
                                        </YStack>
                                    </YStack>
                                </YStack>
                            )}
                            <YStack gap={12} padding={24}>
                                <Button
                                    variant={ButtonVariant.PREMIUM}
                                    size={ButtonSize.LARGE}
                                    text={primaryAction.text}
                                    onPress={primaryAction.onPress}
                                />
                                {secondaryAction != null && (
                                    <Button
                                        variant={ButtonVariant.PREMIUM_TERTIARY}
                                        size={ButtonSize.LARGE}
                                        text={secondaryAction.text}
                                        onPress={secondaryAction.onPress}
                                        textProps={{ color: textColor }}
                                    />
                                )}
                            </YStack>
                        </ScrollView>
                    </YStack>
                </YStack>
            </YStack>
        </Modal>
    );
};
